﻿import { useMemo } from "react";
import { Pencil, Trash2, Check, Calendar, PackagePlus } from "lucide-react";

import EditableSelectCell from "@/components/desktop/list/table/cells/SelectCell/EditableSelectCell";
import CustomBadge from "@/components/desktop/badge/CustomBadge";
import CustomProductName from "@/components/desktop/products/product_name/CustomProductName";
import CustomDropdown from "@/components/desktop/list/dropdown/CustomDropdown";
import CustomButton from "@/components/desktop/button/CustomButton";

import { ProductOption, ProductSingleValue } from "@/components/desktop/products/product_option/ProductOption";

import { useProgramApplicationColumns } from "./useProgramApplicationColumns";
import { useProgramDetailColumns } from "./useProgramDetailColumns";

export function usePhytosanitaryProgramColumns(
	lookups,
	handleSave,
	editingRowId,
	setEditingRowId,
	handleRemoveProduct,
	handleCreateOA,
	{ handleDeleteApplication, handleAddProduct, estateParentId = 0 },
) {
	const applicationColumns = useProgramApplicationColumns(lookups, handleSave, editingRowId);
	const detailColumns = useProgramDetailColumns(lookups, handleSave, editingRowId);

	const productColumn = useMemo(
		() => [
			{
				Header: "Producto",
				accessor: "product_text",
				width: "14%",
				disableSortBy: true,
				Cell: ({ row }) => {
					const productID = row.original.product_id;
					const productName = row.original.product_text;
					const productCode = row.original.product_code;
					const typeDetail = row.original.type_detail;
					const subGroup = row.original.sub_group;

					const rowId = `${row.original.id}_${row.original.application_number}`;
					const isEditing = editingRowId === rowId;

					const typeDetailUpper = typeDetail?.toUpperCase();

					const colorPalette = [
						"#3b82f6",
						"#10b981",
						"#f59e0b",
						"#ef4444",
						"#8b5cf6",
						"#ec4899",
						"#06b6d4",
						"#84cc16",
						"#f97316",
						"#6366f1",
						"#14b8a6",
						"#a855f7",
					];

					const getColorForSubGroup = (subGroupValue) => {
						if (!subGroupValue) return "transparent";

						let hash = 0;
						const str = String(subGroupValue);
						for (let i = 0; i < str.length; i++) {
							hash = str.charCodeAt(i) + ((hash << 5) - hash);
						}

						const index = Math.abs(hash) % colorPalette.length;
						return colorPalette[index];
					};

					const borderColor = getColorForSubGroup(subGroup);

					const typeDetailOptions = [
						{ value: "y", label: "y" },
						{ value: "o", label: "o" },
					];

					if (isEditing) {
						const rowEstateParentId = row.original.estate_parent_id > 0 ? row.original.estate_parent_id : estateParentId;
						const filteredProducts = (lookups?.products_customers || []).filter(
							(product) => product.estate_parent_id === 0 || product.estate_parent_id === rowEstateParentId,
						);

						return (
							<div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
								<EditableSelectCell
									value={productID}
									row={row}
									fieldName="product_id"
									onSave={handleSave}
									options={filteredProducts}
									placeholder="Escribe para buscar..."
									noOptionsMessage={(inputValue) => (!inputValue || inputValue.length < 2 ? "Escribe al menos 2 caracteres" : "Sin opciones")}
									filterOption={(option, inputValue) => {
										if (!inputValue || inputValue.length < 2) return false;
										return option.label.toLowerCase().includes(inputValue.toLowerCase());
									}}
									components={{
										Option: ProductOption,
										SingleValue: ProductSingleValue,
									}}
								/>
								<EditableSelectCell
									value={typeDetail}
									row={row}
									fieldName="type_detail"
									onSave={handleSave}
									options={typeDetailOptions}
									placeholder="y / o"
								/>
								<Trash2
									size={16}
									style={{ color: "#ef4444", cursor: "pointer", marginTop: "2px" }}
									onClick={() => handleRemoveProduct(row.original)}
								/>
							</div>
						);
					}

					return (
						<div
							style={{
								display: "flex",
								alignItems: "center",
								gap: "8px",
								maxWidth: "100%",
								borderLeft: `3px solid ${borderColor}`,
								paddingLeft: subGroup ? "8px" : "0",
								marginLeft: "-8px",
							}}
						>
							<div style={{ flex: 1, overflow: "hidden", whiteSpace: "normal", textOverflow: "ellipsis" }}>
								<CustomProductName
									product={{
										code: productCode,
										value: productID,
										label: productName,
									}}
								/>
							</div>
							{typeDetailUpper === "Y" && <CustomBadge variant="primary">{typeDetail.toLowerCase()}</CustomBadge>}
							{typeDetailUpper === "O" && <CustomBadge variant="warning">{typeDetail.toLowerCase()}</CustomBadge>}
						</div>
					);
				},
			},
		],
		[lookups, handleSave, editingRowId, handleRemoveProduct, estateParentId],
	);

	const actionColumns = useMemo(() => {
		const handleEdit = (row) => {
			setEditingRowId(`${row.id}_${row.application_number}`);
		};

		const handleCancelEdit = () => {
			setEditingRowId(null);
		};

		return [
			{
				Header: "Acción",
				accessor: "actions",
				meta: { cellClassName: "tdCenter" },
				disableSortBy: true,
				width: "5%",
				Cell: ({ row }) => {
					const rowId = `${row.original.id}_${row.original.application_number}`;
					const isEditing = editingRowId === rowId;

					if (isEditing) {
						return (
							<CustomButton
								variant="ghost"
								size="sm"
								startIcon={<Check size={20} />}
								onClick={handleCancelEdit}
								ariaLabel="Finalizar edición"
								style={{ color: "#81ba43" }}
							/>
						);
					}

					return (
						<CustomDropdown
							title="Acciones"
							items={[
								{
									label: "Editar",
									action: () => handleEdit(row.original),
									icon: Pencil,
								},
								{ separator: true },
								{
									label: "Nueva OA",
									action: () => {
										handleCreateOA(row.original);
									},
									icon: Calendar,
								},
								{
									label: "Agregar Producto",
									action: () => handleAddProduct(row.original),
									icon: PackagePlus,
								},
								{ separator: true },
								{
									label: "Eliminar",
									action: () => handleDeleteApplication(row.original),
									icon: Trash2,
									danger: true,
								},
							]}
							align="right"
						/>
					);
				},
			},
			{
				Header: "",
				accessor: "_expand",
				disableSortBy: true,
				width: "3%",
				Cell: () => null,
			},
		];
	}, [editingRowId, setEditingRowId, handleCreateOA, handleDeleteApplication, handleAddProduct]);

	return useMemo(
		() => [...applicationColumns, ...productColumn, ...detailColumns, ...actionColumns],
		[applicationColumns, productColumn, detailColumns, actionColumns],
	);
}
